/**
 * Finding a URL that was typed as plain text, not marked up.
 *
 * Unlike a mention there is no wire format to parse — a link is just a URL
 * sitting in the message body exactly as someone typed or pasted it. Nothing
 * is rewritten at send time; this only decides, at render time, which spans
 * of an already-stored body should become a clickable `<a>`.
 */
/** http(s) only, greedy up to the next whitespace or a character a URL
 *  cannot contain unescaped. Trailing punctuation is trimmed afterwards —
 *  doing it in the pattern itself cannot tell "a link followed by a comma"
 *  from "a link whose path legitimately ends in a paren". */
const URL_START = /https?:\/\/[^\s<>"]+/g;
/** Punctuation a sentence would end a URL with, never part of one. */
const TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":", "'", '"']);
/**
 * Every URL in a message body, in the order they appear.
 *
 * A trailing `)` is trimmed only when it has no matching `(` earlier in the
 * match — "(see https://x.com/page)" drops the wrapping paren, but
 * "https://en.wikipedia.org/wiki/Foo_(bar)" keeps its own, since the count of
 * `(` in the candidate is not exceeded by the count of `)`.
 */
export function parseLinks(body) {
    const found = [];
    // matchAll rather than a stateful exec loop: the regex is module-level and
    // `lastIndex` would leak between calls.
    for (const match of body.matchAll(URL_START)) {
        let url = match[0];
        let end = match.index + url.length;
        while (url.length > 0) {
            const last = url[url.length - 1];
            if (last === ")") {
                const opens = (url.match(/\(/g) ?? []).length;
                const closes = (url.match(/\)/g) ?? []).length;
                if (closes <= opens)
                    break; // belongs to the URL, not the sentence
                url = url.slice(0, -1);
                end -= 1;
                continue;
            }
            if (TRAILING_PUNCTUATION.has(last)) {
                url = url.slice(0, -1);
                end -= 1;
                continue;
            }
            break;
        }
        if (url.length > 0)
            found.push({ url, start: match.index, end });
    }
    return found;
}
//# sourceMappingURL=links.js.map